import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ContractTable = ({
  contracts,
  selectedContracts,
  onSelectionChange,
  onSort,
  sortConfig,
  onViewAnalysis,
  onRegenerateEmail,
  onDownloadDocument,
  onDownloadCalendar,
  onRowClick
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);

  const getStatusBadge = (status) => {
    const configs = {
      processed: {
        bg: 'bg-success/10',
        text: 'text-success',
        border: 'border-success/20',
        icon: 'CheckCircle'
      },
      pending: {
        bg: 'bg-warning/10',
        text: 'text-warning',
        border: 'border-warning/20',
        icon: 'Clock'
      },
      error: {
        bg: 'bg-error/10',
        text: 'text-error',
        border: 'border-error/20',
        icon: 'AlertCircle'
      }
    };

    const config = configs[status] || configs.pending;

    return (
      <div className={`
        inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium
        ${config.bg} ${config.text} ${config.border} border
      `}>
        <Icon name={config.icon} size={12} color="currentColor" />
        <span className="capitalize">{status}</span>
      </div>
    );
  };

  const handleSort = (column) => {
    // Safety check for sortConfig
    if (!sortConfig) {
      onSort({ column, direction: 'asc' });
      return;
    }
    const direction = sortConfig.column === column && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSort({ column, direction });
  };

  const getSortIcon = (column) => {
    // Safety check for undefined sortConfig
    if (!sortConfig || !sortConfig.column) {
      return <Icon name="ArrowUpDown" size={14} color="var(--color-muted-foreground)" />;
    }
    if (sortConfig.column !== column) {
      return <Icon name="ArrowUpDown" size={14} color="var(--color-muted-foreground)" />;
    }
    return (
      <Icon
        name={sortConfig.direction === 'asc' ? "ArrowUp" : "ArrowDown"}
        size={14}
        color="var(--color-primary)"
      />
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(contracts.map(contract => contract.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectContract = (contractId, checked) => {
    if (checked) {
      onSelectionChange([...selectedContracts, contractId]);
    } else {
      onSelectionChange(selectedContracts.filter(id => id !== contractId));
    }
  };

  const isAllSelected = contracts.length > 0 && selectedContracts.length === contracts.length;
  const isPartiallySelected = selectedContracts.length > 0 && selectedContracts.length < contracts.length;

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isPartiallySelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('filename')}
                  className="flex items-center space-x-2 font-heading font-medium text-gray-900 hover:text-blue-600 transition-colors duration-200"
                >
                  <span>Contract File</span>
                  {getSortIcon('filename')}
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('clientName')}
                  className="flex items-center space-x-2 font-heading font-medium text-gray-900 hover:text-blue-600 transition-colors duration-200"
                >
                  <span>Client Name</span>
                  {getSortIcon('clientName')}
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('propertyAddress')}
                  className="flex items-center space-x-2 font-heading font-medium text-gray-900 hover:text-blue-600 transition-colors duration-200"
                >
                  <span>Property Address</span>
                  {getSortIcon('propertyAddress')}
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('uploadDate')}
                  className="flex items-center space-x-2 font-heading font-medium text-gray-900 hover:text-blue-600 transition-colors duration-200"
                >
                  <span>Upload Date</span>
                  {getSortIcon('uploadDate')}
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <span className="font-heading font-medium text-gray-900">Status</span>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => handleSort('lastActivity')}
                  className="flex items-center space-x-2 font-heading font-medium text-gray-900 hover:text-blue-600 transition-colors duration-200"
                >
                  <span>Last Activity</span>
                  {getSortIcon('lastActivity')}
                </button>
              </th>
              <th className="text-center px-4 py-3">
                <span className="font-heading font-medium text-gray-900">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((contract) => (
              <tr
                key={contract.id}
                className={`
                  border-b border-border transition-colors duration-200
                  ${selectedContracts.includes(contract.id) ? 'bg-primary/5' : ''}
                  ${hoveredRow === contract.id ? 'bg-muted/30' : 'hover:bg-muted/30'}
                `}
                onMouseEnter={() => setHoveredRow(contract.id)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td className="px-4 py-4">
                  <Checkbox
                    checked={selectedContracts.includes(contract.id)}
                    onChange={(e) => handleSelectContract(contract.id, e.target.checked)}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="FileText" size={16} color="var(--color-primary)" />
                    </div>
                    <div>
                      <button
                        onClick={() => onRowClick && onRowClick(contract.id)}
                        className="font-body font-medium truncate max-w-48 text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200 text-left"
                      >
                        {contract.filename}
                      </button>
                      <div className="text-xs text-gray-500">
                        {contract.fileSize}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="font-body font-medium text-gray-900">
                    {contract.clientName}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="font-body max-w-64 truncate text-gray-900">
                    {contract.propertyAddress}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="font-body text-gray-900">
                    {contract.uploadDate}
                  </div>
                </td>
                <td className="px-4 py-4">
                  {getStatusBadge(contract.status)}
                </td>
                <td className="px-4 py-4">
                  <div className="font-body text-gray-600">
                    {contract.lastActivity}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className={`
                    flex items-center space-x-1 transition-opacity duration-200
                    ${hoveredRow === contract.id ? 'opacity-100' : 'opacity-0'}
                  `}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewAnalysis(contract.id)}
                      iconName="Eye"
                      iconSize={14}
                      className="text-muted-foreground hover:text-primary"
                      title="View Analysis"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDownloadDocument(contract.id)}
                      iconName="Download"
                      iconSize={14}
                      className="text-muted-foreground hover:text-accent"
                      title="Download Document"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDownloadCalendar(contract.id)}
                      iconName="Calendar"
                      iconSize={14}
                      className="text-muted-foreground hover:text-success"
                      title="Download Calendar"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card Layout */}
      <div className="lg:hidden">
        {contracts.map((contract) => (
          <div
            key={contract.id}
            className={`
              p-4 border-b border-border last:border-b-0 transition-colors duration-200
              ${selectedContracts.includes(contract.id) ? 'bg-primary/5' : ''}
              ${hoveredRow === contract.id ? 'bg-muted/30' : 'hover:bg-muted/30'}
            `}
            onMouseEnter={() => setHoveredRow(contract.id)}
            onMouseLeave={() => setHoveredRow(null)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedContracts.includes(contract.id)}
                  onChange={(e) => handleSelectContract(contract.id, e.target.checked)}
                />
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={18} color="var(--color-primary)" />
                </div>
              </div>
              {getStatusBadge(contract.status)}
            </div>

            <div className="space-y-2 mb-4">
              <button
                onClick={() => onRowClick && onRowClick(contract.id)}
                className="font-body font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200 text-left"
              >
                {contract.filename}
              </button>
              <div className="text-sm text-gray-600">
                Client: {contract.clientName}
              </div>
              <div className="text-sm text-gray-600">
                Property: {contract.propertyAddress}
              </div>
              <div className="text-sm text-gray-600">
                Uploaded: {contract.uploadDate}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-600">
                Last activity: {contract.lastActivity}
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewAnalysis(contract.id)}
                  iconName="Eye"
                  iconSize={14}
                  className="text-muted-foreground hover:text-primary"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDownloadDocument(contract.id)}
                  iconName="Download"
                  iconSize={14}
                  className="text-muted-foreground hover:text-accent"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDownloadCalendar(contract.id)}
                  iconName="Calendar"
                  iconSize={14}
                  className="text-muted-foreground hover:text-success"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {contracts.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="FileText" size={24} color="#999999" />
          </div>
          <h3 className="font-heading font-medium mb-2 text-gray-900">
            No contracts found
          </h3>
          <p className="text-gray-600">
            Try adjusting your filters or upload a new contract to get started.
          </p>
        </div>
      )}
    </div>
  );
};

export default ContractTable;